import React, { useCallback, useEffect, useState } from 'react'
import BarChartIcon from '@material-symbols/svg-300/outlined/bar_chart.svg'
import DashboardIcon from '@material-symbols/svg-300/outlined/dashboard.svg'
import EditCalendarOutlinedIcon from '@material-symbols/svg-300/outlined/edit_calendar.svg'
import ScreenSearchDesktopOutlinedIcon from '@material-symbols/svg-300/outlined/screen_search_desktop.svg'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router'
import ExpandMoreIcon from '@material-symbols/svg-300/outlined/expand_more.svg'
import { Link } from 'react-router-dom'

import SkillfitLogo from '../SkillfitLogo/SkillfitLogo'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import AssumeClientMenu from '../AssumeClientMenu/AssumeClientMenu'
import Button from '../basics/Button/Button'
import NavLink from '../basics/NavLink'

import styles from './MainNav.module.scss'

export default React.memo(function MainNav() {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser, logout } = useSession()

  const pathname = useLocation().pathname.replace(/^\/[^/]*/, '')

  // Path of the currently expanded level 2 navigation
  const [subnavPath, setSubnavPath] = useState(pathname)

  // Set expanded sublevel navigation to current URL path on navigation
  useEffect(() => setSubnavPath(pathname), [pathname])

  // Open sublevel navigation by button
  const handleOpenSubnav = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      const newPath = e.currentTarget.dataset.subnavPath!
      setSubnavPath((prev) => (matchPath(newPath, prev) ? '' : newPath))
    },
    [setSubnavPath]
  )

  return (
    <nav className={styles.mainNav} aria-label={t('main navigation')}>
      <ul>
        <li>
          <Link aria-hidden tabIndex={-1} to={routes.dashboard.url({})}>
            <SkillfitLogo />
          </Link>
        </li>
        <li className={styles.assumeClientMenu}>
          <AssumeClientMenu />
        </li>
        <li>
          <NavLink to={routes.dashboard.url({})} end>
            <DashboardIcon /> {t('dashboard.page title')}
          </NavLink>
        </li>
        <li>
          <NavLink to={routes.courses.url({})}>
            <ScreenSearchDesktopOutlinedIcon /> {t('browse courses.page title')}
          </NavLink>
        </li>
        <li>
          <NavLink to={routes.insights.url({})} end>
            <BarChartIcon /> {t('insights.page title')}
          </NavLink>
        </li>
        <li className={matchPath('/manage/*', subnavPath) && styles.subnavOpen}>
          <NavLink to={routes.manageUsers.url({})} activeTo={routes.manage.url({})}>
            <EditCalendarOutlinedIcon /> {t('manage')}
          </NavLink>
          <button
            aria-hidden
            tabIndex={-1}
            className={styles.expandButton}
            data-subnav-path="/manage/*"
            onClick={handleOpenSubnav}
          >
            <ExpandMoreIcon />
          </button>
          <div className={styles.level2}>
            <ul>
              <li>
                <NavLink to={routes.manageUsers.url({})}>{t('users')}</NavLink>
              </li>
              <li>
                <NavLink to={routes.manageGroups.url({})}>{t('groups')}</NavLink>
              </li>
            </ul>
          </div>
        </li>
        {currentUser && (
          <li className={styles.screenReaderOnly}>
            <NavLink to={routes.manageUser.url({ uuid: currentUser.uuid })} end>
              {t('myProfile')}
            </NavLink>
          </li>
        )}
        <li className={styles.screenReaderOnly}>
          <Button plain onClick={logout}>
            {t('login password.logout')}
          </Button>
        </li>
      </ul>
    </nav>
  )
})
